import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { IKeyFrame, IVideoROI, RoiChange } from '@analysis/domain';

export interface VideoRoiState {
  videoRois: EntityState<IVideoROI>;
  labelIndex: Map<string, Set<string>>; // label -> Set of videoRoiIds
  assetIndex: Map<string, Set<string>>; // asset -> Set of videoRoiIds
  labelCounts: Map<string, number>;
  totalCount: number;
  changedVideoRois: Map<string, RoiChange>;
  loading: boolean;
  error: string | undefined;
  selectedVideoRois: Set<string>;
  syncingChanges: boolean;
}

@Injectable()
export class VideoRoiStore extends ComponentStore<VideoRoiState> {
  public readonly adapter: EntityAdapter<IVideoROI>;

  constructor() {
    const adapter = createEntityAdapter<IVideoROI>();
    super({
      videoRois: adapter.getInitialState(),
      labelIndex: new Map(),
      assetIndex: new Map(),
      labelCounts: new Map(),
      totalCount: 0,
      changedVideoRois: new Map(),
      loading: false,
      error: undefined,
      selectedVideoRois: new Set(),
      syncingChanges: false,
    });

    this.adapter = adapter;
  }

  // #region Selectors

  public readonly videoRois$ = this.select((state) => state.videoRois.entities);
  public readonly labelIndex$ = this.select((state) => state.labelIndex);
  public readonly assetIndex$ = this.select((state) => state.assetIndex);
  public readonly labelCounts$ = this.select((state) => state.labelCounts);
  public readonly totalCount$ = this.select((state) => state.totalCount);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);
  public readonly selectedVideoRois$ = this.select((state) => state.selectedVideoRois);
  public readonly syncingChanges$ = this.select((state) => state.syncingChanges);
  public readonly changedVideoRois$ = this.select((state) => state.changedVideoRois);
  public readonly unsynced$ = this.select((state) => state.changedVideoRois.size > 0);
  public readonly synced$ = this.select(this.unsynced$, (unsynced) => !unsynced);

  public readonly nonEmptyLabelIndex$ = this.select(this.labelIndex$, (labelIndex) => {
    const result = new Map<string, Set<string>>();
    labelIndex.forEach((videoRois, id) => videoRois.size > 0 && result.set(id, videoRois));
    return result;
  });

  public readonly selectedVideoRoisEntities$ = this.select(
    this.videoRois$,
    this.selectedVideoRois$,
    (videoRois, selectedIds) =>
      Array.from(selectedIds)
        .map((id) => videoRois[id])
        .filter(Boolean)
  );

  // #endregion

  // #region Updaters

  private readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: loading ? undefined : state.error,
  }));

  private readonly setError = this.updater((state, error: string | undefined) => ({
    ...state,
    error,
    loading: false,
  }));

  public readonly setVideoRois = this.updater((state, videoRois: IVideoROI[]) => {
    let newState = { ...state };

    newState.labelIndex = new Map();
    newState.assetIndex = new Map();
    newState.labelCounts = new Map();
    newState.totalCount = 0;

    for (const videoRoi of videoRois) {
      newState = this.indexVideoRoi(newState, videoRoi);
    }

    return {
      ...newState,
      videoRois: this.adapter.setAll(videoRois, state.videoRois),
    };
  });

  public readonly addOrUpdateVideoRois = this.updater((state, videoRois: IVideoROI[]) => {
    let newState = { ...state };

    const videoRoisToAdd: IVideoROI[] = [];
    const videoRoisToUpdate: { id: string; changes: Partial<IVideoROI> }[] = [];

    for (const videoRoi of videoRois) {
      const existingVideoRoi = state.videoRois.entities[videoRoi.id];

      if (!existingVideoRoi) {
        videoRoisToAdd.push(videoRoi);
      } else {
        // Remove from index to re-index with updated data
        newState = this.removeVideoRoiFromIndex(newState, existingVideoRoi);
        videoRoisToUpdate.push({
          id: videoRoi.id,
          changes: videoRoi,
        });
      }

      newState = this.indexVideoRoi(newState, videoRoi);
    }

    const updatedVideoRois = this.adapter.updateMany(videoRoisToUpdate, state.videoRois);
    const addedVideoRois = this.adapter.addMany(videoRoisToAdd, updatedVideoRois);

    return {
      ...newState,
      videoRois: addedVideoRois,
    };
  });

  public readonly removeVideoRois = this.updater((state, videoRoiIds: string[]) => {
    let newState = { ...state };

    for (const videoRoiId of videoRoiIds) {
      const videoRoi = state.videoRois.entities[videoRoiId];
      if (videoRoi) {
        newState = this.removeVideoRoiFromIndex(newState, videoRoi);
      }
    }

    return {
      ...newState,
      videoRois: this.adapter.removeMany(videoRoiIds, state.videoRois),
    };
  });

  public readonly setFocusedVideoRoi = this.updater((state, videoRoiId: string | undefined) => ({
    ...state,
    focusedVideoRoi: videoRoiId,
  }));

  public readonly selectVideoRois = this.updater((state, videoRoiIds: string[]) => {
    const newSelectedVideoRois = new Set(state.selectedVideoRois);
    videoRoiIds.forEach((id) => newSelectedVideoRois.add(id));

    return {
      ...state,
      selectedVideoRois: newSelectedVideoRois,
    };
  });

  public readonly deselectVideoRois = this.updater((state, videoRoiIds: string[]) => {
    const newSelectedVideoRois = new Set(state.selectedVideoRois);
    videoRoiIds.forEach((id) => newSelectedVideoRois.delete(id));

    return {
      ...state,
      selectedVideoRois: newSelectedVideoRois,
    };
  });

  public readonly clearSelectedVideoRois = this.updater((state) => ({
    ...state,
    selectedVideoRois: new Set(),
  }));

  public readonly updateVideoRoiLabels = this.updater(
    (state, { videoRoiId, labels }: { videoRoiId: string; labels: Map<string, number> }) => {
      const videoRoi = state.videoRois.entities[videoRoiId];
      if (!videoRoi) return state;

      let newState = this.removeVideoRoiFromIndex(state, videoRoi);

      const updatedVideoRoi = {
        ...videoRoi,
        keyframes: videoRoi.keyframes.map((keyframe) => ({
          ...keyframe,
          labels: new Map(labels),
        })),
      };

      newState = this.indexVideoRoi(newState, updatedVideoRoi);

      return {
        ...newState,
        videoRois: this.adapter.updateOne({ id: videoRoiId, changes: updatedVideoRoi }, state.videoRois),
      };
    }
  );

  public readonly removeAllLabels = this.updater((state, videoRoiIds: string[]) => {
    let newState = { ...state };

    for (const videoRoiId of videoRoiIds) {
      const videoRoi = state.videoRois.entities[videoRoiId];
      if (!videoRoi) continue;

      newState = this.removeVideoRoiFromIndex(newState, videoRoi);

      const updatedVideoRoi = {
        ...videoRoi,
        keyframes: videoRoi.keyframes.map((keyframe) => ({
          ...keyframe,
          labels: new Map(),
        })),
      };

      newState = this.indexVideoRoi(newState, updatedVideoRoi);

      newState = {
        ...newState,
        videoRois: this.adapter.updateOne({ id: videoRoiId, changes: updatedVideoRoi }, newState.videoRois),
      };
    }

    return newState;
  });

  public readonly updateVideoRoiTime = this.updater(
    (state, { videoRoiId, changes }: { videoRoiId: string; changes: { t1?: number; t2?: number } }) => {
      const videoRoi = state.videoRois.entities[videoRoiId];
      if (!videoRoi || !videoRoi.keyframes?.length) return state;

      const timestamps = videoRoi.keyframes.map((kf) => kf.timestamp);
      const currT1 = Math.min(...timestamps);
      const currT2 = Math.max(...timestamps);
      const duration = currT2 - currT1;

      const newT1 = changes.t1 ?? currT1;
      const newT2 = changes.t2 ?? newT1 + duration;

      const firstKeyframe = videoRoi.keyframes[0];
      const newKeyframes: IKeyFrame[] = [
        {
          ...firstKeyframe,
          timestamp: newT1,
        },
        {
          ...firstKeyframe,
          timestamp: newT2,
        },
      ];

      return {
        ...state,
        videoRois: this.adapter.updateOne({ id: videoRoiId, changes: { keyframes: newKeyframes } }, state.videoRois),
      };
    }
  );

  // TODO: Implement revert logic for video ROIs

  public readonly revertChanges = this.updater((state, videoRoiIds: string[]) => {
    const newChangedVideoRois = new Map(state.changedVideoRois);
    videoRoiIds.forEach((id) => newChangedVideoRois.delete(id));

    return {
      ...state,
      changedVideoRois: newChangedVideoRois,
    };
  });

  // #endregion

  // #region Effects

  public readonly loadVideoRois = this.effect((videoRois$: Observable<IVideoROI[]>) =>
    videoRois$.pipe(
      tap(() => this.setLoading(true)),
      tap({
        next: (videoRois) => {
          this.setVideoRois(videoRois as IVideoROI[]);
          this.setLoading(false);
        },
        error: (error) => {
          this.setError(error.message);
        },
      })
    )
  );

  public readonly addVideoRois = this.effect((videoRois$: Observable<IVideoROI[]>) =>
    videoRois$.pipe(
      tap({
        next: (videoRois) => {
          this.addOrUpdateVideoRois(videoRois as IVideoROI[]);
        },
        error: (error) => {
          this.setError(error.message);
        },
      })
    )
  );

  public readonly removeVideoRoisEffect = this.effect((videoRoiIds$: Observable<string[]>) =>
    videoRoiIds$.pipe(
      tap({
        next: (videoRoiIds) => {
          this.removeVideoRois(videoRoiIds as string[]);
        },
        error: (error) => {
          this.setError(error.message);
        },
      })
    )
  );

  // #endregion

  // #region Private Methods

  private indexVideoRoi(state: VideoRoiState, videoRoi: IVideoROI): VideoRoiState {
    const newLabelIndex = new Map(state.labelIndex);
    const newAssetIndex = new Map(state.assetIndex);
    const newLabelCounts = new Map(state.labelCounts);

    // Update asset index
    const roisForAsset = newAssetIndex.get(videoRoi.assetId) ?? new Set();
    roisForAsset.add(videoRoi.id);
    newAssetIndex.set(videoRoi.assetId, roisForAsset);

    // Collect all unique labels from all keyframes
    const allLabels = new Set<string>();
    videoRoi.keyframes.forEach((keyframe) => {
      keyframe.labels.forEach((_, labelId) => {
        allLabels.add(labelId);
      });
    });

    // Update label index
    allLabels.forEach((labelId) => {
      if (!newLabelIndex.has(labelId)) {
        newLabelIndex.set(labelId, new Set());
      }
      const labelSet = newLabelIndex.get(labelId);
      if (labelSet) {
        labelSet.add(videoRoi.id);
      }
    });

    // Update label counts
    allLabels.forEach((labelId) => {
      const currentCount = newLabelCounts.get(labelId) || 0;
      newLabelCounts.set(labelId, currentCount + 1);
    });

    return {
      ...state,
      labelIndex: newLabelIndex,
      assetIndex: newAssetIndex,
      labelCounts: newLabelCounts,
      totalCount: state.totalCount + 1,
    };
  }

  private removeVideoRoiFromIndex(state: VideoRoiState, videoRoi: IVideoROI): VideoRoiState {
    const newLabelIndex = new Map(state.labelIndex);
    const newAssetIndex = new Map(state.assetIndex);
    const newLabelCounts = new Map(state.labelCounts);

    // Update asset index
    const roisForAsset = newAssetIndex.get(videoRoi.assetId);
    if (roisForAsset) {
      roisForAsset.delete(videoRoi.id);
      if (roisForAsset.size === 0) {
        newAssetIndex.delete(videoRoi.assetId);
      } else {
        newAssetIndex.set(videoRoi.assetId, roisForAsset);
      }
    }

    const allLabels = new Set<string>();
    videoRoi.keyframes.forEach((keyframe) => {
      keyframe.labels.forEach((_, labelId) => {
        allLabels.add(labelId);
      });
    });

    allLabels.forEach((labelId) => {
      const videoRoiSet = newLabelIndex.get(labelId);
      if (videoRoiSet) {
        videoRoiSet.delete(videoRoi.id);
        if (videoRoiSet.size === 0) {
          newLabelIndex.delete(labelId);
        }
      }
    });

    allLabels.forEach((labelId) => {
      const currentCount = newLabelCounts.get(labelId) || 0;
      const newCount = Math.max(0, currentCount - 1);
      if (newCount === 0) {
        newLabelCounts.delete(labelId);
      } else {
        newLabelCounts.set(labelId, newCount);
      }
    });

    return {
      ...state,
      labelIndex: newLabelIndex,
      assetIndex: newAssetIndex,
      labelCounts: newLabelCounts,
      totalCount: Math.max(0, state.totalCount - 1),
    };
  }

  // #endregion
}
